import { useEffect, useRef } from "react";

import { Stack } from "@mui/material";
import { MAX_TABLE_FILTERS } from "@simplyk/common";

import { BaseFilterInput, FilterCombinator } from "@/components/FilterBox/types";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TableFilterBuilder } from "./TableFilterBuilder";
import { useFilterBoxStateContext } from "@/components/FilterBox/FilterBoxStateContext";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { Add, ArrowBackward } from "@/icons/outlined";

interface TableFiltersPanelContentProps<T extends BaseFilterInput> {
  onChange: (filters: T[], combinator: FilterCombinator) => void;
  onCombinatorChange?: (combinator: FilterCombinator) => void;
  onReset: () => void;
  onEmpty?: () => void;
  focusedFilterId?: string;
  onFilterFocused?: () => void;
  openedFrom?: string;
}

export const TableFiltersPanelContent = <T extends BaseFilterInput>({
  onChange,
  onReset,
  onEmpty,
  focusedFilterId,
  onFilterFocused,
  onCombinatorChange,
  openedFrom,
}: TableFiltersPanelContentProps<T>) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const { validFilters, areAllFiltersValid, filterCombinator } = useFilterBoxStateContext<T>();
  useEffect(() => {
    if (!isSmallScreen) {
      onCombinatorChange?.(filterCombinator);
    }
  }, [filterCombinator, isSmallScreen, onCombinatorChange]);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const panel = panelRef.current;
    if (!focusedFilterId || !panel) {
      return;
    }
    const focusValue = () => {
      const valueContainer = Array.from(panel.querySelectorAll<HTMLElement>("[data-filter-id]")).find(
        (element) => element.dataset.filterId === focusedFilterId
      );
      const input = valueContainer?.querySelector<HTMLElement>(
        "input:not([disabled]):not([type='hidden']), textarea:not([disabled]), [role='combobox']:not([disabled]):not([aria-disabled='true'])"
      );
      if (input) {
        window.cancelAnimationFrame(frame);
        observer.disconnect();
        input.focus();
        onFilterFocused?.();
      }
    };
    const observer = new MutationObserver(focusValue);
    const frame = window.requestAnimationFrame(focusValue);
    observer.observe(panel, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled", "aria-disabled"],
    });
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [focusedFilterId, onFilterFocused]);

  return (
    <Stack
      ref={panelRef}
      width="100%"
      data-test="payment-filters-panel"
      sx={(theme) =>
        isSmallScreen
          ? {
              paddingX: 3,
              paddingTop: 2,
              flexGrow: 1,
              "& > .MuiStack-root": { flexGrow: 1 },
              "& > .MuiStack-root > .MuiBox-root, & > .MuiStack-root > .MuiBox-root > .MuiBox-root": {
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
              },
              "& > .MuiStack-root > .MuiBox-root": { backgroundColor: "transparent", padding: 0 },
              "& > .MuiStack-root > .MuiBox-root > .MuiBox-root > .MuiBox-root": { gap: 1, flexGrow: 1 },
              "& > .MuiStack-root > .MuiBox-root > .MuiBox-root > .MuiBox-root > .MuiBox-root": {
                padding: 2,
                borderRadius: theme.radius(1),
                backgroundColor: theme.palette.surface.neutral.supershy,
              },
              "& > .MuiStack-root > .MuiBox-root > .MuiBox-root > .MuiBox-root > .MuiBox-root > .MuiBox-root": {
                gridTemplateColumns: "minmax(0, 1fr) auto",
                gridTemplateAreas: '"type delete" "operation operation" "values values"',
              },
              "& .MuiInputBase-root": { height: theme.spacing(4.5), ...theme.typography.body2 },
              "&& .MuiInputBase-input": { paddingY: 0.5, fontSize: theme.typography.body1.fontSize },
              "& .MuiSelect-icon": { width: theme.spacing(2.5), height: theme.spacing(2.5) },
              "& [data-test='filter-delete-button']": { width: theme.spacing(4.75), height: theme.spacing(4.5) },
            }
          : { "& [data-test='filter-row']": { gridTemplateColumns: "1fr 160px 2fr auto" } }
      }
    >
      <TableFilterBuilder
        openedFrom={openedFrom}
        initiallyShowTypeSelector={!focusedFilterId}
        onEmpty={onEmpty}
        maxFilters={MAX_TABLE_FILTERS}
        renderFooter={({ addFilter, resetFilters, isAddingFilter, canAddFilter, saveAsListButton }) => (
          <Stack flexGrow={1}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <DashboardButton
                variant="outlined"
                vibe="neutral"
                size="small"
                startIcon={<Add />}
                sx={{ paddingLeft: 1.5, paddingRight: 2, height: 30 }}
                slotProps={{
                  startIcon: { sx: (theme) => ({ width: theme.spacing(2.5), height: theme.spacing(2.5) }) },
                }}
                disabled={!canAddFilter}
                data-test="payment-filter-add"
                onClick={addFilter}
              >
                {t("dashboard", "paymentsPage.table.filters.add")}
              </DashboardButton>
              {saveAsListButton}
              {!isSmallScreen && (
                <DashboardButton
                  variant="text"
                  vibe="neutral"
                  size="small"
                  endIcon={<ArrowBackward />}
                  sx={{ paddingLeft: 2, paddingRight: 1, height: 30, marginInlineStart: "auto" }}
                  slotProps={{
                    endIcon: { sx: (theme) => ({ width: theme.spacing(2.5), height: theme.spacing(2.5) }) },
                  }}
                  data-test="payment-filter-reset"
                  onClick={() => {
                    resetFilters();
                    onReset();
                  }}
                >
                  {t("dashboard", "paymentsPage.table.filters.resetAll")}
                </DashboardButton>
              )}
            </Stack>
            {isSmallScreen && (
              <Stack
                gap={1}
                padding={2}
                marginX={-3}
                marginTop="auto"
                paddingTop={4}
                sx={(theme) => ({ backgroundColor: theme.palette.surface.neutral.white })}
              >
                <DashboardButton
                  variant="filled"
                  vibe="brand"
                  fullWidth
                  disabled={!areAllFiltersValid || isAddingFilter}
                  data-test="payment-filter-apply"
                  onClick={() => onChange(validFilters, filterCombinator)}
                  sx={(theme) => ({ height: theme.spacing(4.5) })}
                >
                  {t("dashboard", "paymentsPage.table.filters.seeResults")}
                </DashboardButton>
                <DashboardButton
                  variant="text"
                  vibe="neutral"
                  fullWidth
                  data-test="payment-filter-reset"
                  onClick={() => {
                    resetFilters();
                    onReset();
                  }}
                  sx={(theme) => ({ height: theme.spacing(4.5) })}
                >
                  {t("dashboard", "paymentsPage.table.filters.clearQuick")}
                </DashboardButton>
              </Stack>
            )}
          </Stack>
        )}
      />
    </Stack>
  );
};
